import { useState, useEffect } from 'react';
import { Modal, Form, Select, DatePicker, Input, Alert, Button, RadioGroup, Descriptions, message } from '../../ui';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { MultiFileUploadField } from '../../components/MultiFileUploadField';

const MODES = ['UPI', 'NEFT', 'RTGS', 'IMPS', 'CASH', 'CHEQUE', 'CARD', 'OTHER'];
const inr2 = (v: any) => `₹${Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/**
 * Settle the advance excess left after a short-close (retailer paid for more than the order was finally
 * billed). Accounts/Admin/SA (PAYMENT.REFUND) choose:
 *   • CREDIT — keep it as the retailer's Advance-in-credit (no money moves, no GST correction). Default.
 *   • REFUND — record a cash refund already made outside the platform (+ proof); posts the REFUND ledger debit.
 * Backend: POST /orders/:id/settle-advance (flag-gated, hard-validates refund mode/date/proof).
 */
export function SettleAdvanceModal({ open, onClose, orderId, orderNo, excess, billed, paidNet, onDone }: {
  open: boolean; onClose: () => void; orderId: string; orderNo?: string;
  excess: number; billed?: number; paidNet?: number; onDone: () => void;
}) {
  const [form] = Form.useForm();
  const [mode, setMode] = useState<'REFUND' | 'CREDIT'>('CREDIT');

  useEffect(() => {
    if (open) { setMode('CREDIT'); form.setFieldsValue({ refundMode: 'UPI', refundDate: dayjs(), reason: undefined, refundProofUrls: undefined }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const submit = useMutation({
    mutationFn: (v: any) => request<any>({
      url: `/orders/${orderId}/settle-advance`, method: 'POST',
      data: mode === 'CREDIT'
        ? { mode: 'CREDIT' }
        : { mode: 'REFUND', refundMode: v.refundMode, refundDate: v.refundDate.format('YYYY-MM-DD'), refundProofUrls: v.refundProofUrls, reason: v.reason },
    }),
    onSuccess: () => { message.success(mode === 'REFUND' ? 'Advance refund recorded' : 'Kept as advance-in-credit'); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to settle advance'),
  });

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Settle advance — order ${orderNo ?? ''}`} width={620}>
      <Descriptions size="small" column={1} bordered className="mb-3">
        <Descriptions.Item label="Advance paid (net)">{inr2(paidNet)}</Descriptions.Item>
        <Descriptions.Item label="Billed (this order)">{inr2(billed)}</Descriptions.Item>
        <Descriptions.Item label="Excess to settle"><b style={{ color: '#1677ff' }}>{inr2(excess)}</b></Descriptions.Item>
      </Descriptions>
      <Alert
        type="info"
        className="mb-3"
        title="The retailer overpaid vs what this order was finally billed."
        description="Return the excess as a refund, or keep it as the retailer's Advance-in-credit for their next order (no money moves, no GST correction)."
      />
      <RadioGroup
        value={mode}
        onChange={(value) => setMode(value as 'REFUND' | 'CREDIT')}
        options={[{ value: 'CREDIT', label: 'Keep as credit' }, { value: 'REFUND', label: 'Refund' }]}
        inline
        className="mb-3"
      />

      {mode === 'CREDIT' ? (
        <>
          <Alert
            type="success"
            className="mb-3"
            title={`${inr2(excess)} stays as the retailer's Advance-in-credit.`}
            description="Usable against their next order. Nothing leaves the business; the ledger already carries it as credit."
          />
          <Button variant="primary" loading={submit.isPending} block onClick={() => submit.mutate({})}>
            Keep {inr2(excess)} as credit
          </Button>
        </>
      ) : (
        <Form form={form} layout="vertical" onFinish={(v) => submit.mutate(v)}>
          <Alert
            type="warning"
            className="mb-3"
            title="Recording a refund you have already paid outside the platform"
            description="This captures the reversal + proof and posts the REFUND ledger debit — the platform does not move money."
          />
          <Form.Item name="refundMode" label="Refund mode" rules={[{ required: true }]}>
            <Select options={MODES.map((m) => ({ value: m }))} />
          </Form.Item>
          <Form.Item name="refundDate" label="Refund date" rules={[{ required: true }]}>
            <DatePicker className="w-full" />
          </Form.Item>
          <Form.Item name="reason" label="Reason / notes">
            <Input.TextArea rows={2} maxLength={255} placeholder="e.g. 20 units short-closed — advance excess returned via UPI" />
          </Form.Item>
          <Form.Item
            name="refundProofUrls"
            label="Refund proof"
            rules={[{ required: true, type: 'array', min: 1, message: 'Attach the reversal / transfer receipt' }]}
            extra="Required — the reversal screenshot or receipt."
          >
            <MultiFileUploadField category="receipt" buttonLabel="Upload refund proof" />
          </Form.Item>
          <Button variant="danger" type="submit" loading={submit.isPending} block>
            Refund {inr2(excess)}
          </Button>
        </Form>
      )}
    </Modal>
  );
}
